/**
 * Settings: baby & family profile, preferences, partner pairing,
 * install-to-home-screen help, and about/erase. Reached via the gear;
 * leaves via the back button (useNav().back(), returning to the tab it
 * was opened from).
 */
import { useState, type ReactNode } from 'react';
import { useNav } from '../App';
import { useT } from '../i18n/i18n';
import { useActions, useApp } from '../lib/store';
import { toLocalIso } from '../lib/time';
import { lbOzToGrams } from '../lib/units';
import { Icons, Segmented } from '../components/ui';
import PairingPanel from '../components/PairingPanel';
import DataSafetySection from '../components/DataSafetySection';
import {
  OFFICE_PHONE_DISPLAY,
  OFFICE_PHONE_TEL,
  PRACTICE_NAME,
  WEBSITE_URL,
} from '../lib/config';
import { ACCENTS } from '../lib/types';
import type { Locale, ThemePref, VolumeUnit, WeightUnit } from '../lib/types';

const INPUT_CLS = 'w-full rounded-2xl border border-line bg-bg px-4 py-3 text-ink';
const LABEL_CLS = 'mb-1 block text-sm font-bold text-ink-soft';

const G_PER_LB = lbOzToGrams(1, 0);
const G_PER_OZ = lbOzToGrams(0, 1);

function gramsToLbOzParts(grams: number): { lb: number; oz: number } {
  const lb = Math.floor(grams / G_PER_LB);
  const oz = Math.round((grams - lb * G_PER_LB) / G_PER_OZ);
  return oz === 16 ? { lb: lb + 1, oz: 0 } : { lb, oz };
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="card p-4">
      <h2 className="mb-3 text-base">{title}</h2>
      {children}
    </section>
  );
}

/** lb+oz or grams inputs; remounted (via key) when the weight unit changes. */
function BirthWeightFields({ disabled }: { disabled: boolean }) {
  const t = useT();
  const { settings, profile } = useApp();
  const { updateProfile } = useActions();

  const grams = profile?.birthWeightG;
  const parts = grams != null ? gramsToLbOzParts(grams) : null;
  const [lbStr, setLbStr] = useState(parts ? String(parts.lb) : '');
  const [ozStr, setOzStr] = useState(parts ? String(parts.oz) : '');
  const [gStr, setGStr] = useState(grams != null ? String(Math.round(grams)) : '');

  const commit = () => {
    let next: number | undefined;
    if (settings.weightUnit === 'lboz') {
      const lb = parseFloat(lbStr);
      const oz = parseFloat(ozStr);
      if (!Number.isNaN(lb) || !Number.isNaN(oz)) {
        const g = lbOzToGrams(Number.isNaN(lb) ? 0 : lb, Number.isNaN(oz) ? 0 : oz);
        next = g > 0 ? Math.round(g) : undefined;
      }
    } else {
      const g = parseFloat(gStr);
      next = !Number.isNaN(g) && g > 0 ? Math.round(g) : undefined;
    }
    if (next !== profile?.birthWeightG) void updateProfile({ birthWeightG: next });
  };

  return (
    <div>
      <span className={LABEL_CLS}>{t.settings.baby.birthWeight}</span>
      {settings.weightUnit === 'lboz' ? (
        <div className="flex items-center gap-2">
          <input
            type="number"
            inputMode="decimal"
            min="0"
            value={lbStr}
            disabled={disabled}
            onChange={(e) => setLbStr(e.target.value)}
            onBlur={commit}
            aria-label={t.settings.units.lb}
            className={INPUT_CLS}
          />
          <span className="text-sm font-bold text-ink-soft">{t.settings.units.lb}</span>
          <input
            type="number"
            inputMode="decimal"
            min="0"
            max="15"
            value={ozStr}
            disabled={disabled}
            onChange={(e) => setOzStr(e.target.value)}
            onBlur={commit}
            aria-label={t.settings.units.oz}
            className={INPUT_CLS}
          />
          <span className="text-sm font-bold text-ink-soft">{t.settings.units.oz}</span>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <input
            type="number"
            inputMode="numeric"
            min="0"
            value={gStr}
            disabled={disabled}
            onChange={(e) => setGStr(e.target.value)}
            onBlur={commit}
            aria-label={t.settings.units.g}
            className={INPUT_CLS}
          />
          <span className="text-sm font-bold text-ink-soft">{t.settings.units.g}</span>
        </div>
      )}
    </div>
  );
}

function BabyFamilySection() {
  const t = useT();
  const { settings, profile } = useApp();
  const { updateProfile } = useActions();

  // No local profile yet (a partner device before its first sync): editing
  // would have to invent a base profile, which could clobber mom's real one
  // via last-writer-wins. Lock the fields until the profile arrives.
  const locked = !profile;

  const [nickname, setNickname] = useState(profile?.babyNickname ?? '');
  const [birthLocal, setBirthLocal] = useState(
    profile?.birthAt ? toLocalIso(new Date(profile.birthAt)).slice(0, 16) : '',
  );
  const [dueDate, setDueDate] = useState(profile?.dueDate ?? '');
  const [goals, setGoals] = useState(profile?.feedingGoals ?? '');

  return (
    <Section title={t.settings.baby.title}>
      <div className="space-y-4">
        {locked ? (
          <p className="text-sm text-ink-soft">{t.settings.baby.waitingForSync}</p>
        ) : null}
        <div>
          <label className={LABEL_CLS} htmlFor="set-nickname">
            {t.settings.baby.nickname}
          </label>
          <input
            id="set-nickname"
            type="text"
            value={nickname}
            maxLength={60}
            disabled={locked}
            onChange={(e) => setNickname(e.target.value)}
            onBlur={() => {
              const next = nickname.trim() || undefined;
              if (next !== profile?.babyNickname) void updateProfile({ babyNickname: next });
            }}
            placeholder={t.settings.baby.nicknamePlaceholder}
            className={INPUT_CLS}
          />
        </div>

        <div>
          <label className={LABEL_CLS} htmlFor="set-birth">
            {t.settings.baby.birthDate}
          </label>
          <input
            id="set-birth"
            type="datetime-local"
            value={birthLocal}
            max={toLocalIso(new Date()).slice(0, 16)}
            disabled={locked}
            onChange={(e) => setBirthLocal(e.target.value)}
            onBlur={() => {
              if (!birthLocal) return;
              const birth = new Date(birthLocal);
              // A future birth date would render "Day 0" (or negative days).
              if (Number.isNaN(birth.getTime()) || birth.getTime() > Date.now()) return;
              const iso = toLocalIso(birth);
              if (iso !== profile?.birthAt) void updateProfile({ birthAt: iso, phase: 'baby' });
            }}
            className={INPUT_CLS}
          />
        </div>

        <div>
          <label className={LABEL_CLS} htmlFor="set-due">
            {t.settings.baby.dueDate}
          </label>
          <input
            id="set-due"
            type="date"
            value={dueDate}
            disabled={locked}
            onChange={(e) => setDueDate(e.target.value)}
            onBlur={() => {
              const next = dueDate || undefined;
              if (next !== profile?.dueDate) void updateProfile({ dueDate: next });
            }}
            className={INPUT_CLS}
          />
        </div>

        <BirthWeightFields key={settings.weightUnit} disabled={locked} />

        <div>
          <label className={LABEL_CLS} htmlFor="set-goals">
            {t.settings.baby.feedingGoals}
          </label>
          <textarea
            id="set-goals"
            rows={3}
            value={goals}
            maxLength={1000}
            disabled={locked}
            onChange={(e) => setGoals(e.target.value)}
            onBlur={() => {
              const next = goals.trim() || undefined;
              if (next !== profile?.feedingGoals) void updateProfile({ feedingGoals: next });
            }}
            placeholder={t.settings.baby.feedingGoalsPlaceholder}
            className={`${INPUT_CLS} resize-none`}
          />
        </div>
      </div>
    </Section>
  );
}

function PreferencesSection() {
  const t = useT();
  const { settings } = useApp();
  const { setSettings } = useActions();

  return (
    <Section title={t.settings.prefs.title}>
      <div className="space-y-4">
        <div>
          <span className={LABEL_CLS}>{t.settings.prefs.language}</span>
          <Segmented<Locale>
            value={settings.locale}
            onChange={(v) => void setSettings({ locale: v })}
            label={t.settings.prefs.language}
            options={[
              { value: 'en', label: t.settings.prefs.english },
              { value: 'es', label: t.settings.prefs.spanish },
            ]}
          />
        </div>
        <div>
          <span className={LABEL_CLS}>{t.settings.prefs.volumeUnit}</span>
          <Segmented<VolumeUnit>
            value={settings.volumeUnit}
            onChange={(v) => void setSettings({ volumeUnit: v })}
            label={t.settings.prefs.volumeUnit}
            options={[
              { value: 'oz', label: t.settings.prefs.oz },
              { value: 'ml', label: t.settings.prefs.ml },
            ]}
          />
        </div>
        <div>
          <span className={LABEL_CLS}>{t.settings.prefs.weightUnit}</span>
          <Segmented<WeightUnit>
            value={settings.weightUnit}
            onChange={(v) => void setSettings({ weightUnit: v })}
            label={t.settings.prefs.weightUnit}
            options={[
              { value: 'lboz', label: t.settings.prefs.lboz },
              { value: 'g', label: t.settings.prefs.grams },
            ]}
          />
        </div>
        <div>
          <span className={LABEL_CLS}>{t.settings.prefs.theme}</span>
          <Segmented<ThemePref>
            value={settings.theme}
            onChange={(v) => void setSettings({ theme: v })}
            label={t.settings.prefs.theme}
            options={[
              { value: 'auto', label: t.settings.prefs.themeAuto },
              { value: 'light', label: t.settings.prefs.themeLight },
              { value: 'dark', label: t.settings.prefs.themeDark },
            ]}
          />
        </div>
        <div>
          <span className={LABEL_CLS}>{t.settings.prefs.accent}</span>
          <AccentPicker />
        </div>
      </div>
    </Section>
  );
}

/**
 * Color swatches. Each swatch previews its own palette by setting
 * data-accent on itself, so the colors come from the stylesheet rather than
 * being duplicated in TypeScript.
 */
function AccentPicker() {
  const { settings } = useApp();
  const { setSettings } = useActions();
  const t = useT();

  return (
    <div role="radiogroup" aria-label={t.settings.prefs.accent} className="flex flex-wrap gap-3">
      {ACCENTS.map((accent) => {
        const selected = settings.accent === accent;
        return (
          <button
            key={accent}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={t.settings.prefs.accentNames[accent]}
            title={t.settings.prefs.accentNames[accent]}
            data-accent={accent}
            onClick={() => void setSettings({ accent })}
            className={`flex h-11 w-11 items-center justify-center rounded-full transition-transform active:scale-95 ${
              selected ? 'ring-2 ring-primary ring-offset-2 ring-offset-surface' : ''
            }`}
            style={{ background: 'var(--a-pri-l)' }}
          >
            {selected ? (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--a-on-l)"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-5 w-5"
                aria-hidden="true"
              >
                <path d="m5 12.5 4.5 4.5L19 7.5" />
              </svg>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function InstallSection() {
  const t = useT();
  const [open, setOpen] = useState(false);

  return (
    <section className="card p-4">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex min-h-[44px] w-full items-center justify-between gap-2 text-left"
      >
        <h2 className="text-base">{t.settings.install.title}</h2>
        <span className="shrink-0 text-ink-faint">
          {Icons.chevronRight(`h-5 w-5 transition-transform ${open ? 'rotate-90' : ''}`)}
        </span>
      </button>
      {open ? (
        <div className="animate-float-up mt-2 space-y-4">
          <p className="text-sm text-ink-soft">{t.settings.install.intro}</p>
          <div>
            <h3 className="mb-1 text-sm">{t.settings.install.ios}</h3>
            <ol className="list-decimal space-y-1 pl-5 text-sm text-ink-soft">
              {t.settings.install.iosSteps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </div>
          <div>
            <h3 className="mb-1 text-sm">{t.settings.install.android}</h3>
            <ol className="list-decimal space-y-1 pl-5 text-sm text-ink-soft">
              {t.settings.install.androidSteps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </div>
          <p className="border-t border-line pt-3 text-xs leading-relaxed text-ink-faint">
            {t.settings.install.finePrint}
          </p>
        </div>
      ) : null}
    </section>
  );
}

function AboutSection() {
  const t = useT();
  const nav = useNav();
  const { eraseAllData } = useActions();
  const [armed, setArmed] = useState(false);

  return (
    <Section title={t.settings.about.title}>
      <div className="space-y-4">
        <div>
          <h3 className="text-base">{PRACTICE_NAME}</h3>
          <p className="text-sm text-ink-soft">{t.common.practiceTagline}</p>
        </div>

        <a
          href={`tel:${OFFICE_PHONE_TEL}`}
          className="flex min-h-[48px] items-center gap-3 rounded-2xl bg-accent-soft px-4 py-3 font-bold text-accent"
        >
          {Icons.phone('h-5 w-5')}
          <span>
            {t.settings.about.call}: {OFFICE_PHONE_DISPLAY}
          </span>
        </a>
        <a
          href={WEBSITE_URL}
          target="_blank"
          rel="noreferrer"
          className="flex min-h-[48px] items-center gap-3 rounded-2xl bg-surface-2 px-4 py-3 font-bold text-primary"
        >
          {Icons.chevronRight('h-5 w-5')}
          <span>{t.settings.about.website}</span>
        </a>

        <button
          type="button"
          onClick={() => nav.go('privacy')}
          className="flex min-h-[44px] w-full items-center justify-between gap-2 text-left font-bold text-primary"
        >
          {t.settings.about.privacy}
          {Icons.chevronRight('h-5 w-5')}
        </button>
        <p className="text-xs text-ink-faint">{t.common.disclaimer}</p>

        {armed ? (
          <div className="animate-float-up space-y-3 rounded-2xl bg-bad-soft p-4">
            <p className="text-sm font-bold text-bad">{t.settings.about.eraseWarning}</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setArmed(false)}
                className="btn-secondary min-h-[44px] flex-1 py-2.5"
              >
                {t.common.actions.cancel}
              </button>
              <button
                type="button"
                onClick={() => void eraseAllData()}
                className="min-h-[44px] flex-1 rounded-full bg-surface py-2.5 font-bold text-bad"
              >
                {t.settings.about.eraseConfirm}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setArmed(true)}
            className="min-h-[48px] w-full rounded-full bg-bad-soft py-3 font-bold text-bad"
          >
            {t.settings.about.erase}
          </button>
        )}
      </div>
    </Section>
  );
}

export default function SettingsScreen() {
  const t = useT();
  const { back } = useNav();
  const { profile } = useApp();

  return (
    <div className="min-h-dvh bg-bg">
      <header className="sticky top-0 z-20 border-b border-line bg-bg/90 px-4 pb-2.5 pt-3 backdrop-blur">
        <div className="mx-auto flex max-w-lg items-center gap-1">
          <button
            type="button"
            onClick={back}
            aria-label={t.common.actions.back}
            className="-ml-2 rounded-full p-2.5 text-ink-soft hover:bg-surface-2"
          >
            {Icons.chevronRight('h-6 w-6 rotate-180')}
          </button>
          <h1 className="text-xl">{t.settings.title}</h1>
        </div>
      </header>

      <main className="mx-auto max-w-lg space-y-4 px-4 py-4">
        {/* Remount when the profile first arrives via sync, so the fields
            prefill instead of staying blank from the locked render. */}
        <BabyFamilySection key={profile ? 'profile' : 'no-profile'} />
        <PreferencesSection />
        <Section title={t.settings.partner.title}>
          <PairingPanel />
        </Section>
        <DataSafetySection />
        <InstallSection />
        <AboutSection />
      </main>
    </div>
  );
}
